import {
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { PreworkDetailViewModel } from "../../types/PreworkDetailsViewModel";
import { RenameFiles } from "../../api/preworkDetailApi/preworkDetailApi";
import { useSnackbar } from "../Common/SnackbarContext";
import LocalStorageUtil from "../../utils/localStorage";

interface RenameProps {
  isOpen: boolean;
  onClose: (isCancelClick: boolean, tabValue?: number) => void;
  title: string;
  selectedData: any;
  tabValue?: number;
}

const RenamePopup: React.FC<RenameProps> = ({
  isOpen,
  onClose,
  title,
  selectedData,
  tabValue,
}) => {
  const [oldFileName, setOldFileName] = useState<string>("");
  const [newFilename, setNewFilename] = useState<string>("");
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );
  const [remark, setRemark] = useState<string>("");
  const [remarkValidationMessage, setRemarkValidationMessage] = useState<
    string | null
  >(null);
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  useEffect(() => {
    setLoading(true);
    setOldFileName(selectedData.Filename === "" || selectedData.Filename === undefined ? selectedData.DownloadFileName : selectedData.Filename);
    setNewFilename(selectedData.Filename === "" || selectedData.Filename === undefined ? selectedData.DownloadFileName : selectedData.Filename);
    setLoading(false);
  }, [selectedData]);

  const handleUpdate = async () => {
    try {
      setLoading(true);
      if (newFilename === "") {
        setValidationMessage("Please enter a valid name");
      } else if (remark === "") {
        setRemarkValidationMessage("Please enter the remark");
      } else {
        //API Call
        const params: PreworkDetailViewModel = {
          CheckBoxDocumentId: selectedData.childDocumentID === undefined ? selectedData.DocumentID : selectedData.childDocumentID,
          CheckBoxDocumentStreamId: selectedData.childFileDocumentStreamID === undefined ? selectedData.DocumentStreamID : selectedData.childFileDocumentStreamID,
          AccepectId: undefined,
          DocumentStatus: undefined,
          AccntId: Number(LocalStorageUtil.getItem("accountId")),
          UserId: Number(LocalStorageUtil.getItem("userId")),
          Remarks: remark,
          ParentFileName: undefined,
          ChildFilename: newFilename,
          ParentChildId: undefined,
          ChildCount: undefined,
          PageType: undefined,
          ChildFileType: selectedData.childFileType === undefined ? selectedData.FileType : selectedData.childFileType,
          QueueType: undefined,
          ProjDocumentId: "",
          ContextName: LocalStorageUtil.getItem("contextName") ?? "",
        };
        const response = await RenameFiles(params);
        if (response) {
          showSnackbar("File Renamed Successfully");
          setValidationMessage(null);
          setRemarkValidationMessage(null);
          onClose(false, tabValue);
        } else {
          console.log("Error in renaming file");
        }
      }
    } catch (error) {
      console.log("Error in renaming file", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  //Close the dialog box
  const handleCloseClick = () => {
    onClose(true);
  };

  const handleNewFilenameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      const value = event.target.value;
      setNewFilename(value);
    } catch (error) {
      console.error("Error in New filename", error);
      throw error;
    }
  };

  const handleRemarkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = event.target.value;
      setRemark(value);
    } catch (error) {
      console.error("Error in remark change", error);
      throw error;
    } finally {
      setRemarkValidationMessage(null);
    }
  };

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={handleCloseClick}
        maxWidth="sm"
        PaperProps={{
          style: {
            height: "auto",
            width: "20%",
          },
        }}
      >
        <DialogTitle
          style={{
            backgroundColor: "var(--primary-color)",
            color: "var(--aggrid-text-color)",
            fontSize: "16px", 
            fontWeight: 500, 
            padding: "12px 16px", 
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            className="col-sm-12"
            style={{
              display: "flex",
              justifyContent: "left",
              alignItems: "center",
            }}
          >
            <div
              className="col-sm-11"
              style={{
                display: "flex",
                alignItems: "left",
                gap: "10px",
                color: "var(--aggrid-text-color)",
              }}
            >
              {title}
            </div>
            <div
              className="col-sm-1"
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "right",
              }}
            >
              {" "}
              <Tooltip title="Close">
                <IconButton
                  onClick={() => onClose(true)}
                  style={{
                    cursor: "pointer",
                    alignItems: "flex-end",
                    color: "var(--aggrid-text-color)",
                    width: "20px",
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "60%",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <div className="row">
              <div
                className="col-sm-12"
                style={{
                  marginTop: "1em",
                  fontSize: "0.7rem",
                  fontWeight: "bold",
                }}
              >
                Existing File Name
              </div>
              <div className="col-sm-12" style={{ marginTop: "10px" }}>
                <TextField
                  value={oldFileName}
                  fullWidth
                  style={{ height: "50px" }}
                  InputProps={{
                    readOnly: true,

                    style: {
                      backgroundColor: "var(--aggrid-background-color)",
                      marginTop: ".1em",
                      height: "3em",
                      fontSize: "0.7em",
                    },
                  }}
                />
              </div>
              <div
                className="col-sm-12"
                style={{ fontSize: "0.7rem", fontWeight: "bold" }}
              >
                New File Name *
              </div>
              <div className="col-sm-12" style={{ marginTop: "8px" }}>
                <TextField
                  id="outlined-basic"
                  variant="outlined"
                  value={newFilename}
                  error={!!validationMessage}
                  helperText={validationMessage}
                  fullWidth
                  onChange={handleNewFilenameChange}
                  style={{ height: "50px" }}
                  InputProps={{
                    style: {
                      backgroundColor: "var(--aggrid-background-color)",
                      marginTop: ".1em",
                      height: "3em",
                      fontSize: "0.7em",
                    },
                  }}
                />
              </div>
              <div
                className="col-sm-12"
                style={{ fontSize: "0.7rem", fontWeight: "bold" }}
              >
                Remark *
              </div>
              <div className="col-sm-12" style={{ marginTop: "8px" }}>
                <TextField
                  multiline
                  minRows={10}
                  aria-label="maxWidth"
                  maxRows={3}
                  style={{
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                  }}
                  placeholder="Enter Remark text here"
                  value={remark}
                  error={!!remarkValidationMessage}
                  helperText={remarkValidationMessage}
                  onChange={handleRemarkChange}
                  inputProps={{
                    style: {
                      backgroundColor: "var(--aggrid-background-color)",
                      marginTop: ".1em",
                      height: "3.5vh",
                      fontSize: "0.7rem",
                    },
                  }}
                />
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleUpdate}
            style={{ fontSize: "14px", height: "2.1em", padding: "4px 6px",border: "1px solid #144979", }}
          >
            Update
          </Button>
          <Button
            style={{ fontSize: "14px", height: "2.1em", padding: "4px 6px",border: "1px solid #144979", }}
            onClick={handleCloseClick}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default RenamePopup;
